import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, type BoardRow } from "../api.ts";
import { money, range, rel, STATUS, wa, digits } from "../format.ts";
import { Empty } from "../ui.tsx";

export type Lane = "requested" | "awaiting_payment" | "verify" | "confirmed" | "in_stay";
export const LANES: { id: Lane; label: string; hint: string }[] = [
  { id: "requested", label: "À confirmer", hint: "Vérifier la disponibilité auprès du propriétaire" },
  { id: "awaiting_payment", label: "Paiement attendu", hint: "Disponible — le client doit payer" },
  { id: "verify", label: "À vérifier", hint: "Preuve de paiement reçue : contrôler le compte" },
  { id: "confirmed", label: "Confirmés", hint: "Récupérer les clés, puis les remettre" },
  { id: "in_stay", label: "En séjour", hint: "Clés remises" },
];

const laneOf = (b: BoardRow): Lane[] => {
  const lanes: Lane[] = [];
  if (b.payments.some((p) => p.kind !== "refund" && p.status === "submitted")) lanes.push("verify");
  if (b.status === "requested" || b.status === "confirmed" || b.status === "in_stay") lanes.push(b.status);
  if (b.status === "awaiting_payment" && !lanes.includes("verify")) lanes.push("awaiting_payment");
  return lanes;
};

export function useBoard() {
  const [rows, setRows] = useState<BoardRow[] | null>(null);
  const [err, setErr] = useState(false);
  const load = useCallback(async () => {
    try { setRows(await api<BoardRow[]>("/v1/staff/bookings")); setErr(false); } catch { setErr(true); }
  }, []);
  useEffect(() => { void load(); const i = setInterval(() => void load(), 20_000); return () => clearInterval(i); }, [load]);
  return { rows, err, reload: load };
}

export function Board({ rows, lane, err }: { rows: BoardRow[] | null; lane: Lane; err: boolean }) {
  const list = useMemo(() => (rows ?? []).filter((b) => laneOf(b).includes(lane)).sort((a, b) => Date.parse(a.createdAt) - Date.parse(b.createdAt)), [rows, lane]);
  const meta = LANES.find((l) => l.id === lane)!;
  return (
    <section>
      <header className="pagehead"><h1>{meta.label}</h1><p className="muted small">{meta.hint}</p></header>
      {err && <p className="err small">Connexion perdue — nouvel essai automatique.</p>}
      {rows === null && !err && <div className="skel" />}
      {rows && list.length === 0 && <Empty>Rien à traiter ici.</Empty>}
      <ul className="rows">
        {list.map((b) => {
          const due = b.status === "requested" ? b.respondBy : b.status === "awaiting_payment" ? b.payBy : null;
          const late = due ? Date.parse(due) < Date.now() : false;
          return (
            <li key={b.id} className="row">
              <Link to={`/b/${b.id}`} className="main">
                <span className="code num">{b.code}</span>
                <b>{b.listingTitle?.fr ?? "—"}</b>
                <span className="muted num">{range(b.checkIn, b.checkOut)} · {b.nights} n · {b.guests} pers.</span>
              </Link>
              <div className="who">
                <span>{b.guest?.name ?? "—"}</span>
                <span className="muted small">{b.ownerType === "platform" ? "Bien Sekani" : `Hôte : ${b.host?.name ?? "—"}`}</span>
              </div>
              <div className="side">
                <span className="num">{money(b.totalMinor, b.currency)}</span>
                {due && <span className={`small ${late ? "late" : "muted"}`}>{late ? "dépassé " : "avant "}{rel(due)}</span>}
              </div>
              <div className="quick">
                {b.guest?.phone && <a className="chip" href={`tel:+${digits(b.guest.phone)}`}>Appeler</a>}
                {b.guest?.phone && <a className="chip" href={wa(b.guest.phone, `Bonjour ${b.guest.name}, ici Sekani à propos de votre demande ${b.code}.`) ?? "#"} target="_blank" rel="noreferrer">WhatsApp</a>}
              </div>
            </li>
          );
        })}
      </ul>
    </section>
  );
}

export { STATUS };
